import { useEffect, useState, useCallback } from "react";
import { ShieldHalf, Loader2, KeyRound, LogIn, AlertTriangle, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import AdminDashboard from "@/components/admin/AdminDashboard";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#101820] grid place-items-center px-4" data-testid="admin-shell">
    <Seo title="Console" description="Restricted." path="/admin-console" noindex />
    <div className="w-full max-w-md">
      <div className="flex items-center gap-2.5 justify-center mb-6">
        <div className="w-9 h-9 rounded-lg bg-[#E07B2A] grid place-items-center">
          <ShieldHalf size={20} className="text-white" />
        </div>
        <span className="font-extrabold text-white text-[20px] tracking-tight">MTR Console</span>
      </div>
      {children}
    </div>
  </div>
);

function AdminLogin({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/login", { email, password });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <form onSubmit={submit} className="bg-white rounded-2xl p-6 shadow-xl" data-testid="admin-login-form">
        <h1 className="font-extrabold text-navy text-[20px] mb-1">Staff sign in</h1>
        <p className="text-mortar text-[13px] mb-5">Authorized customer-support staff only.</p>
        <label className="label-mtr">Email</label>
        <input type="email" className="input-mtr mb-3" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@measuretheroof.com" required data-testid="admin-login-email" />
        <label className="label-mtr">Password</label>
        <input type="password" className="input-mtr mb-5" value={password} onChange={(e) => setPassword(e.target.value)}
          required data-testid="admin-login-password" />
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="admin-login-submit">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <><LogIn size={18} /> Continue</>}
        </button>
      </form>
    </Shell>
  );
}

function AdminMfaGate({ onDone, who, deviceRemembered }) {
  const [phase, setPhase] = useState(deviceRemembered ? "device" : "sending");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [masked, setMasked] = useState("");

  const sendCode = async () => {
    setPhase("sending");
    try {
      const { data } = await api.post("/admin/mfa/request");
      setMasked(data.email || who);
      setPhase("enter");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send the code.");
      setPhase("enter");
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (deviceRemembered) {
        try {
          await api.post("/admin/unlock-device");
          if (!cancelled) return onDone();
        } catch {
          /* device no longer trusted — fall through to email MFA */
        }
      }
      if (!cancelled) sendCode();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/mfa/verify", { code, remember_device: remember });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid code.");
    } finally {
      setBusy(false);
    }
  };

  if (phase === "device" || phase === "sending") {
    return (
      <Shell>
        <div className="bg-white rounded-2xl p-6 shadow-xl text-center" data-testid="admin-gate-loading">
          <Loader2 className="animate-spin text-[#E07B2A] mx-auto mb-3" size={26} />
          <p className="text-mortar text-[14px]">{phase === "device" ? "Checking this device…" : "Emailing your verification code…"}</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={verify} className="bg-white rounded-2xl p-6 shadow-xl" data-testid="admin-gate-form">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound size={18} className="text-[#E07B2A]" />
          <h1 className="font-extrabold text-navy text-[20px]">Verify it's you</h1>
        </div>
        <p className="text-mortar text-[13px] mb-5 flex items-center gap-1.5">
          <Mail size={13} /> We emailed a 6-digit code to <strong>{masked || who}</strong>. It expires in 30 minutes.
        </p>
        <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} className="input-mtr mb-4 text-center tracking-[0.5em] text-[20px] font-bold"
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" autoFocus required data-testid="admin-gate-code" />
        <label className="flex items-center gap-2 text-[13px] text-navy mb-5">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="admin-gate-remember" />
          Remember this device for 30 days
        </label>
        <button type="submit" disabled={busy || code.length < 6} className="btn-primary w-full" data-testid="admin-gate-submit">
          {busy ? <Loader2 className="animate-spin" size={18} /> : "Verify & continue"}
        </button>
        <button type="button" onClick={sendCode} className="text-[13px] text-mortar hover:text-navy font-semibold mt-4 w-full text-center" data-testid="admin-gate-resend">
          Didn't get it? Resend code
        </button>
      </form>
    </Shell>
  );
}

function NoAccess() {
  const signOut = async () => { try { await api.post("/auth/logout"); } catch {} window.location.reload(); };
  return (
    <Shell>
      <div className="bg-white rounded-2xl p-6 shadow-xl text-center" data-testid="admin-no-access">
        <AlertTriangle size={28} className="text-amber-500 mx-auto mb-3" />
        <h1 className="font-extrabold text-navy text-[20px] mb-1">No console access</h1>
        <p className="text-mortar text-[13px] mb-5">This account isn't authorized for the support console.</p>
        <button onClick={signOut} className="btn-ghost w-full justify-center" data-testid="admin-switch-account">Sign in with a different account</button>
      </div>
    </Shell>
  );
}

export default function AdminConsole() {
  const [state, setState] = useState({ loading: true });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/status");
      setState({ loading: false, ...data });
    } catch {
      setState({ loading: false, authenticated: false, is_staff: false, unlocked: false });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (state.loading) {
    return <Shell><div className="grid place-items-center py-10"><Loader2 className="animate-spin text-white" size={28} /></div></Shell>;
  }
  if (!state.authenticated) return <AdminLogin onDone={refresh} />;
  if (!state.is_staff) return <NoAccess />;
  if (!state.unlocked) return <AdminMfaGate onDone={refresh} who={state.email} deviceRemembered={state.device_remembered} />;
  return <AdminDashboard session={state} onLock={refresh} />;
}
